<script setup lang="ts">
  import { computed, watch } from 'vue';
  import resourceHelper from '@/Utils/resourceHelper';
  import { useContextDataStore } from '@/VueCore/stores/contextDataStore';

  const props = defineProps<{
    title: string,
    isDanger?: boolean,
    isWide?: boolean
  }>();
  const visible = defineModel<boolean>();
  const emit = defineEmits<{(e: 'close'): void, (e: 'open'): void}>();
  const contextData = useContextDataStore();
  const uid = crypto.randomUUID(); // Unique ID of the current modal dialog instance

  const closeTitle: string = resourceHelper.getString('Close');

  watch(visible, val => {
    if (val) {
      setModalContextData();
      emit('open');
    } else {
      contextData.modal.openedDialogs.splice(contextData.modal.openedDialogs.findIndex(d => d.id === uid), 1);
      removeFocus();
      emit('close');
    }
  });

  const showModalContent = computed(() => {
    return visible.value && contextData.activeModalDialog?.id === uid;
  });

  const disableTransitionAnimation = computed(() => {
    // If there are other modal dialogs in the queue and current modal is not active
    // disable animation to immidiately remove the content leaving the space for the other
    // modals. Otherwise there can be 2 modal dialogs displayed during transition animation.
    return contextData.activeModalDialog?.id !== uid && !!contextData.modal.openedDialogs.length;
  })
  
  function setModalContextData() {
    contextData.modal.openedDialogs.push({
      title: props.title,
      isWide: props.isWide,
      id: uid
    });
  }

  function closeModal() {
    visible.value = false;
  }

  function removeFocus() {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }
</script>

<template>
  <teleport
    v-if="contextData.modal.elements.content"
    :to="contextData.modal.elements.content"
  >
    <transition
      mode="out-in"
      :css="!disableTransitionAnimation"
    >
      <div
        v-if="showModalContent"
        ref="content"
        class="modal-content"
      >
        <div
          class="modal-header"
          :class="{ 'modal-header-danger': props.isDanger }"
        >
          <slot
            name="header"
          >
            <h3 class="modal-title fw-bold">
              {{ title }}
            </h3>
            <button
              class="close btn-close"
              :class="isDanger ? 'btn-close-white' : 'btn-close'"
              :aria-label="closeTitle"
              :title="$localize('Close')"
              @click="closeModal"
            />
          </slot>
        </div>
        <div
          class="modal-body"
        >
          <slot>No modal body content. Default slot has not been used.</slot>
        </div>
        <div
          class="modal-footer"
        >
          <slot
            name="footer"
          >
            <button
              class="btn btn-outline-secondary"
              @click="closeModal"
            >
              {{ closeTitle }}
            </button>
          </slot>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style lang="scss" scoped>
/* Keep transitions to ensure the content remains visible during modal animation, preventing it from disappearing before the modal is fully hidden */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>